import React, { useMemo } from 'react'
import {
  Image,
  Button,
  Pill,
  Tooltip,
  TableComponents,
  Skeleton,
  GenericError,
  useModalContext,
} from '@sketch/components'
import { ReactComponent as ExclamationMarkIcon } from '@sketch/icons/exclamation-mark-triangle-64'
import { SKETCH_WEBSITE } from '@sketch/env-config'

import {
  useGetIntegrationIdentitiesQuery,
  GetIntegrationIdentityConnectUrlMutationVariables,
} from '@sketch/gql-types'

import Slack from '@sketch/icons/slack@1x.png'
import SlackRetina from '@sketch/icons/slack@2x.png'

import { useIntegrationIdentityURLHandler } from '../../hooks'
import {
  ConnectIntegrationIdentityModal,
  UnlinkIntegrationIdentityModal,
} from '../../modals'

import {
  Description,
  DescriptionRow,
  QuestionMarkLink,
  QuestionMarkIcon,
  Section,
  Title,
  Wrapper,
  Table,
  IntegrationInfo,
  IntegrationIcon,
  IntegrationName,
  IntegrationStatus,
  ActionCell,
  ErrorWrapper,
  StyledFakeLinkButton,
} from './WorkspaceSettingsIntegrationsIdentity.styles'

const { TableRow, TableCell } = TableComponents

const TABLE_HEADER = [{ label: 'Integration' }, { label: 'Action' }]

type IntegrationProvider =
  GetIntegrationIdentityConnectUrlMutationVariables['provider']

interface IntegrationRow {
  type: 'integration'
  id: string
  name: string
  provider: IntegrationProvider
  icon: string
  icon2x: string
}

interface SkeletonRow {
  type: 'skeleton'
  id: string
}

type TableItem = IntegrationRow | SkeletonRow

const AVAILABLE_INTEGRATIONS: IntegrationRow[] = [
  {
    type: 'integration',
    id: 'slack',
    name: 'Slack',
    provider: 'SLACK',
    icon: Slack,
    icon2x: SlackRetina,
  },
]

// Derive skeleton array from available integrations so we show the correct number of skeletons
const SKELETON_ITEMS: SkeletonRow[] = AVAILABLE_INTEGRATIONS.map(
  (_, index) => ({
    type: 'skeleton',
    id: `skeleton-${index}`,
  })
)

export interface WorkspaceSettingsIntegrationsIdentityProps {
  workspaceId: string
}

export const WorkspaceSettingsIntegrationsIdentity: React.FC<
  WorkspaceSettingsIntegrationsIdentityProps
> = ({ workspaceId }) => {
  useIntegrationIdentityURLHandler()

  const { showModal } = useModalContext()

  const { data, loading, error, refetch } = useGetIntegrationIdentitiesQuery({
    variables: { identifier: workspaceId },
  })

  const workspaceIntegrations = data?.workspace.integrations ?? []
  const myMembership = useMemo(() => {
    return data?.me.workspaceMemberships.entries.find(
      entry => entry.workspace.identifier === workspaceId
    )
  }, [data, workspaceId])

  const getIdentityState = (provider: IntegrationProvider) => {
    const workspaceHasIntegration = workspaceIntegrations.some(
      i => i.integrationProvider === provider
    )

    if (!workspaceHasIntegration) {
      return { state: 'not-enabled' as const, identityId: undefined }
    }

    const identity = myMembership?.integrationIdentities.find(
      i => i.integration.integrationProvider === provider
    )

    return identity
      ? { state: 'connected' as const, identityId: identity.identifier }
      : { state: 'not-connected' as const, identityId: undefined }
  }

  const handleConnect = (integration: IntegrationRow) => {
    showModal(ConnectIntegrationIdentityModal, {
      integrationName: integration.name,
      integrationProvider: integration.provider,
      workspaceId,
    })
  }

  const handleDisconnect = (identityId: string, integrationName: string) => {
    showModal(UnlinkIntegrationIdentityModal, {
      integrationName,
      identityId,
      workspaceId,
    })
  }

  const renderSkeletonRow = (id: string) => (
    <TableRow key={id}>
      <TableCell>
        <IntegrationInfo>
          <Skeleton height="32px" width="32px" />
          <Skeleton height="16px" width="100px" />
        </IntegrationInfo>
      </TableCell>
      <TableCell>
        <ActionCell>
          <Skeleton height="32px" width="80px" />
        </ActionCell>
      </TableCell>
    </TableRow>
  )

  const renderIntegrationRow = (integration: IntegrationRow) => {
    const { state, identityId } = getIdentityState(integration.provider)
    const isConnected = state === 'connected'
    const isEnabled = state !== 'not-enabled'

    const rowContent = (extraProps?: Record<string, unknown>) => (
      <TableRow key={integration.id} {...extraProps}>
        <TableCell>
          <IntegrationInfo>
            <IntegrationIcon>
              <Image
                alt={integration.name}
                src={integration.icon}
                srcSet={`${integration.icon2x} 2x`}
                width="32px"
                height="32px"
              />
            </IntegrationIcon>
            <IntegrationStatus>
              <IntegrationName>{integration.name}</IntegrationName>
              {isConnected && <Pill variant="owner">Connected</Pill>}
              {!isEnabled && (
                <Pill variant="secondary">Not enabled in this workspace</Pill>
              )}
            </IntegrationStatus>
          </IntegrationInfo>
        </TableCell>
        <TableCell>
          <ActionCell>
            {isConnected && (
              <Button
                variant="secondary-untinted"
                size="32"
                onClick={() => handleDisconnect(identityId!, integration.name)}
              >
                Unlink…
              </Button>
            )}
            {isEnabled && !isConnected && (
              <Button
                variant="secondary"
                size="32"
                onClick={() => handleConnect(integration)}
              >
                Connect…
              </Button>
            )}
            {!isEnabled && (
              <Button variant="secondary" size="32" disabled>
                Connect…
              </Button>
            )}
          </ActionCell>
        </TableCell>
      </TableRow>
    )

    if (!isEnabled) {
      return (
        <Tooltip
          key={integration.id}
          content="Ask your Workspace admin to enable this integration"
        >
          {({ ref, onMouseEnter, onMouseLeave }) =>
            rowContent({ ref, onMouseEnter, onMouseLeave })
          }
        </Tooltip>
      )
    }

    return rowContent()
  }

  if (error) {
    return (
      <Section>
        <Title>Connected Accounts</Title>
        <ErrorWrapper>
          <GenericError
            title="Connected Accounts Unavailable"
            description="There was a problem retrieving your connected accounts."
            icon={<ExclamationMarkIcon />}
            iconSize="medium"
            extra={
              <StyledFakeLinkButton variant="primary" onClick={() => refetch()}>
                Try Again
              </StyledFakeLinkButton>
            }
          />
        </ErrorWrapper>
      </Section>
    )
  }

  const items: TableItem[] = loading ? SKELETON_ITEMS : AVAILABLE_INTEGRATIONS

  return (
    <Section>
      <Title>Connected Accounts</Title>
      <DescriptionRow>
        <Description>
          Connect your user accounts for better functionality with integrations
          in your Workspace.
        </Description>
        <QuestionMarkLink
          href={`${SKETCH_WEBSITE}/redirect/web/connected-accounts`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Learn more about connected accounts"
        >
          <QuestionMarkIcon />
        </QuestionMarkLink>
      </DescriptionRow>
      <Wrapper>
        <Table
          header={TABLE_HEADER}
          items={items}
          renderItem={item =>
            item.type === 'skeleton'
              ? renderSkeletonRow(item.id)
              : renderIntegrationRow(item)
          }
          hideHeader
        />
      </Wrapper>
    </Section>
  )
}
